import React, { useEffect, useState } from "react";
import { Plus, CheckCircle, CreditCard, X } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";

const QUICK = [100, 500, 1000, 5000];

export default function WalletView() {
  const { profile, wallet, refreshWallet } = useApp();
  const [txns, setTxns] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [amount, setAmount] = useState(500);

  const loadTxns = () => api.getTransactions(profile.id).then(setTxns).catch(() => {});
  useEffect(() => { if (profile) loadTxns(); }, [profile]);

  const addFunds = async () => {
    if (!amount || amount <= 0) { toast.error("Enter a valid amount"); return; }
    await api.addFunds(profile.id, Number(amount));
    await refreshWallet();
    loadTxns();
    toast.success(`$${Number(amount).toLocaleString()} added to wallet`);
    setShowAdd(false);
  };

  const toggleReload = async (checked) => {
    await api.setAutoReload(profile.id, checked, wallet?.reload_amount);
    await refreshWallet();
  };

  const fmt = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

  return (
    <div className="h-full overflow-y-auto p-6 md:p-10 pb-24 relative hide-scrollbar">
      <div className="max-w-6xl mx-auto space-y-8 fade-in relative z-10">
        <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-4">
          <div>
            <h2 className="text-3xl font-bold text-white tracking-tight font-display">Wallet &amp; Billing</h2>
            <p className="text-sm text-zinc-400 mt-1">Manage your funds, view transactions, and set up auto-reload.</p>
          </div>
          <div className="flex gap-3 text-zinc-500 text-sm items-center"><CreditCard size={22} className="text-indigo-400" /> <span className="font-medium">Powered by Stripe</span></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="glass-panel p-8 rounded-3xl md:col-span-2 relative overflow-hidden group border-green-500/30">
            <div className="absolute top-0 right-0 w-64 h-64 bg-green-500/10 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/3 pointer-events-none" />
            <div className="relative z-10 flex flex-col md:flex-row justify-between md:items-center gap-6">
              <div>
                <h3 className="text-sm font-bold text-zinc-400 uppercase tracking-wider mb-2">Available Balance</h3>
                <div className="text-5xl font-extrabold text-white tracking-tight mb-2">{fmt(wallet?.balance ?? 0)}</div>
                <p className="text-sm text-green-400 flex items-center"><CheckCircle size={15} className="mr-1.5" /> Ready for purchases &amp; leads</p>
              </div>
              <div className="flex flex-col gap-3 min-w-[200px]">
                <button onClick={() => setShowAdd(true)} className="w-full px-5 py-3.5 bg-white text-black rounded-xl text-sm font-bold hover:bg-zinc-200 transition shadow-[0_0_20px_rgba(255,255,255,0.2)] flex justify-center items-center"><Plus size={16} className="mr-2" /> Add Funds</button>
              </div>
            </div>
          </div>

          <div className="glass-panel p-6 rounded-3xl flex flex-col justify-between">
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-sm font-bold text-zinc-400 uppercase tracking-wider">Auto-Reload</h3>
                <Switch checked={!!wallet?.auto_reload} onCheckedChange={toggleReload} />
              </div>
              <p className="text-xs text-zinc-500 leading-relaxed">Automatically add funds when your balance falls below your service fees or lead costs.</p>
            </div>
            <div className="mt-4 p-3 bg-black/40 rounded-xl border border-white/5">
              <div className="flex justify-between text-xs text-zinc-400 mb-2"><span>Reload Amount</span> <span className="text-white font-bold">{fmt(wallet?.reload_amount ?? 100)}</span></div>
              <div className="flex justify-between text-xs text-zinc-400"><span>Payment Method</span> <span className="text-white font-bold">{wallet?.payment_method}</span></div>
            </div>
          </div>
        </div>

        <div className="glass-panel rounded-2xl overflow-hidden mt-8">
          <div className="px-6 py-5 border-b border-white/10 bg-white/5"><h3 className="font-bold text-white">Transaction History</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-zinc-300">
              <thead className="bg-black/40 text-zinc-500 uppercase text-[10px] font-bold tracking-wider border-b border-white/5">
                <tr><th className="px-6 py-4">Date</th><th className="px-6 py-4">Description</th><th className="px-6 py-4 text-right">Amount</th></tr>
              </thead>
              <tbody className="divide-y divide-white/5 bg-black/20">
                {txns.length === 0 && (<tr><td colSpan={3} className="px-6 py-12 text-center text-zinc-500">No transactions yet.</td></tr>)}
                {txns.map((t) => (
                  <tr key={t.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4 text-zinc-400">{new Date(t.date).toLocaleDateString()}</td>
                    <td className="px-6 py-4 text-white">{t.description}</td>
                    <td className={`px-6 py-4 text-right font-bold ${t.amount >= 0 ? "text-green-400" : "text-zinc-300"}`}>{t.amount >= 0 ? "+" : "-"}${Math.abs(t.amount).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
          <div className="glass-panel bg-[#0a0a0b]/95 rounded-3xl shadow-2xl w-full max-w-md relative fade-in overflow-hidden border border-white/10">
            <div className="px-6 py-5 border-b border-white/10 flex justify-between items-center bg-white/5">
              <h3 className="text-lg font-bold text-white">Add Funds</h3>
              <button onClick={() => setShowAdd(false)} className="text-zinc-500 hover:text-white"><X /></button>
            </div>
            <div className="p-8 space-y-5">
              <div className="grid grid-cols-4 gap-2">
                {QUICK.map((q) => (
                  <button key={q} onClick={() => setAmount(q)} className={`py-2.5 rounded-lg text-sm font-bold border transition-colors ${Number(amount) === q ? "bg-primary-600 text-white border-primary-500" : "bg-white/5 text-zinc-300 border-white/10 hover:bg-white/10"}`}>${q}</button>
                ))}
              </div>
              <div>
                <label className="block text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">Custom Amount</label>
                <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full px-5 py-3.5 rounded-xl dark-input" />
              </div>
              <div className="flex items-center justify-between text-xs text-zinc-400 p-3 bg-black/40 rounded-xl border border-white/5">
                <span>Charged to</span><span className="text-white font-bold">{wallet?.payment_method}</span>
              </div>
              <button onClick={addFunds} className="w-full py-3.5 bg-white text-black rounded-xl font-bold hover:bg-zinc-200 transition-colors">Add {`$${Number(amount || 0).toLocaleString()}`}</button>
              <p className="text-[11px] text-zinc-600 text-center">Demo mode: no real charges are made.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
